/**
 * Optional linked PC folder.
 *
 * A directory handle picked once (Chromium File System Access) is remembered in IndexedDB, so
 * `.lakar` files in the user's own folder tree can be listed, opened and written back directly —
 * the file on disk stays authoritative, nothing is copied or downloaded.
 *
 * The link is meant to be permanent: the handle is recalled on every launch, and it stays linked
 * until the user disconnects it. Chrome keeps the `readwrite` grant across visits when the user
 * answers **Allow on every visit**; otherwise the grant comes back as `prompt`, which costs one
 * click — `healWorkspaceOnGesture()` takes that click, never a folder picker.
 */

import { PROJECT_EXTENSION } from './storage';

const DB_NAME = 'lakaran';
const HANDLE_STORE = 'handles';
const WORKSPACE_KEY = 'workspace';

export interface WorkspaceFile {
  name: string;
  size: number;
  modified: number;
}

export interface OpenedFile {
  name: string;
  text: string;
  /** Baseline for external-change detection. */
  modified: number;
  size: number;
}

interface WritableLike {
  write(data: string): Promise<void>;
  close(): Promise<void>;
}

interface FileHandleLike {
  kind: 'file';
  name: string;
  getFile(): Promise<File>;
  createWritable(options?: { keepExistingData?: boolean }): Promise<WritableLike>;
  queryPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
  requestPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
}

interface DirectoryHandleLike {
  kind: 'directory';
  name: string;
  values?(): AsyncIterable<FileHandleLike | DirectoryHandleLike>;
  entries?(): AsyncIterable<[string, FileHandleLike | DirectoryHandleLike]>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FileHandleLike>;
  removeEntry(name: string): Promise<void>;
  queryPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
  requestPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
}

export type { DirectoryHandleLike };

type PickerHost = Window & {
  showDirectoryPicker?: (options?: unknown) => Promise<DirectoryHandleLike>;
};

export function folderLinkingSupported(): boolean {
  return typeof (window as PickerHost).showDirectoryPicker === 'function';
}

// --------------------------------------------------------------- IndexedDB

function openDb(): Promise<IDBDatabase> {
  const upgrade = (request: IDBOpenDBRequest): void => {
    const db = request.result;
    if (!db.objectStoreNames.contains(HANDLE_STORE)) db.createObjectStore(HANDLE_STORE);
  };
  return new Promise((resolve, reject) => {
    // No version: adopt whatever database exists, then make sure it has the handle store. Opening
    // with a fixed version would skip the upgrade on a database that predates the store, and the
    // linked folder would be silently lost on every launch.
    const request = indexedDB.open(DB_NAME);
    request.onupgradeneeded = () => upgrade(request);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB unavailable'));
    request.onsuccess = () => {
      const db = request.result;
      if (db.objectStoreNames.contains(HANDLE_STORE)) {
        resolve(db);
        return;
      }
      const nextVersion = db.version + 1;
      db.close();
      const retry = indexedDB.open(DB_NAME, nextVersion);
      retry.onupgradeneeded = () => upgrade(retry);
      retry.onsuccess = () => resolve(retry.result);
      retry.onerror = () => reject(retry.error ?? new Error('IndexedDB upgrade failed'));
    };
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  if (typeof indexedDB === 'undefined') return null;
  try {
    const db = await openDb();
    return await new Promise<T | null>((resolve, reject) => {
      const tx = db.transaction(HANDLE_STORE, mode);
      const request = run(tx.objectStore(HANDLE_STORE));
      let result: T;
      request.onsuccess = () => {
        result = request.result;
      };
      request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
      // Resolve on commit, not on the request: a handle written just before a reload must survive.
      tx.oncomplete = () => {
        db.close();
        resolve(result);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error ?? new Error('IndexedDB transaction failed'));
      };
      tx.onabort = () => {
        db.close();
        reject(tx.error ?? new Error('IndexedDB transaction aborted'));
      };
    });
  } catch {
    return null;
  }
}

export async function rememberWorkspace(handle: DirectoryHandleLike | null): Promise<void> {
  if (!handle) {
    await withStore<undefined>('readwrite', (store) => store.delete(WORKSPACE_KEY));
    return;
  }
  await withStore<IDBValidKey>('readwrite', (store) => store.put(handle, WORKSPACE_KEY));
}

export async function recallWorkspace(): Promise<DirectoryHandleLike | null> {
  const stored = await withStore<DirectoryHandleLike>('readonly', (store) =>
    store.get(WORKSPACE_KEY),
  );
  return stored && typeof stored.getFileHandle === 'function' ? stored : null;
}

// ----------------------------------------------------------------- picking

/** Asks for a folder, keeps it for later sessions. */
export async function linkFolder(): Promise<DirectoryHandleLike | null> {
  const picker = (window as PickerHost).showDirectoryPicker;
  if (!picker) return null;
  const handle = await picker({ mode: 'readwrite', id: 'lakaran-workspace' });
  await rememberWorkspace(handle);
  await ensureWritePermission(handle);
  return handle;
}

export async function ensureWritePermission(handle: DirectoryHandleLike): Promise<boolean> {
  if (!handle.queryPermission || !handle.requestPermission) return true;
  const descriptor = { mode: 'readwrite' as const };
  try {
    if ((await handle.queryPermission(descriptor)) === 'granted') return true;
    return (await handle.requestPermission(descriptor)) === 'granted';
  } catch {
    return false;
  }
}

/** The grant a stored folder has right now: `granted` needs no click, `prompt` needs one. */
export async function workspacePermission(
  handle: DirectoryHandleLike,
): Promise<PermissionState | 'unsupported'> {
  if (!handle.queryPermission) return 'unsupported';
  try {
    return await handle.queryPermission({ mode: 'readwrite' });
  } catch {
    return 'prompt';
  }
}

/**
 * Re-asks for the folder grant on the **first** click or keypress anywhere, so a folder linked in an
 * earlier session comes back without a picker and without hunting for a button. The link itself is
 * permanent — only `forgetWorkspace()` ends it.
 */
export function healWorkspaceOnGesture(
  handle: DirectoryHandleLike,
  done: (granted: boolean) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};
  const off = (): void => {
    window.removeEventListener('pointerdown', run, true);
    window.removeEventListener('keydown', run, true);
  };
  const run = (): void => {
    off();
    void ensureWritePermission(handle).then(done);
  };
  window.addEventListener('pointerdown', run, true);
  window.addEventListener('keydown', run, true);
  return off;
}

/** Ends the link: the handle is erased from this browser, so no session recalls it again. */
export async function forgetWorkspace(): Promise<void> {
  await rememberWorkspace(null);
}

// ------------------------------------------------------------------ files

/** `.lakar` files inside the linked folder, newest first. */
export async function listFolderProjects(root: DirectoryHandleLike): Promise<WorkspaceFile[]> {
  const out: WorkspaceFile[] = [];
  const entries = root.entries?.() ?? iterateValues(root);
  for await (const [name, handle] of entries) {
    if (handle.kind !== 'file' || !name.toLowerCase().endsWith(PROJECT_EXTENSION)) continue;
    try {
      const file = await handle.getFile();
      out.push({ name, size: file.size, modified: file.lastModified });
    } catch {
      out.push({ name, size: 0, modified: 0 });
    }
  }
  return out.sort((a, b) => b.modified - a.modified);
}

async function* iterateValues(
  root: DirectoryHandleLike,
): AsyncGenerator<[string, FileHandleLike | DirectoryHandleLike]> {
  if (!root.values) return;
  for await (const handle of root.values()) yield [handle.name, handle];
}

export async function readFolderProject(
  root: DirectoryHandleLike,
  name: string,
): Promise<OpenedFile> {
  const handle = await root.getFileHandle(name);
  const file = await handle.getFile();
  return { name: file.name, text: await file.text(), modified: file.lastModified, size: file.size };
}

export async function writeFolderProject(
  root: DirectoryHandleLike,
  name: string,
  text: string,
): Promise<WorkspaceFile> {
  const handle = await root.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();
  try {
    await writable.write(text);
  } finally {
    await writable.close();
  }
  const file = await handle.getFile();
  return { name: file.name, size: file.size, modified: file.lastModified };
}

export async function deleteFolderProject(root: DirectoryHandleLike, name: string): Promise<void> {
  await root.removeEntry(name);
}

/** Renames inside the linked folder by rewriting the payload under the new name. */
export async function renameFolderProject(
  root: DirectoryHandleLike,
  from: string,
  to: string,
): Promise<WorkspaceFile> {
  const text = (await readFolderProject(root, from)).text;
  const saved = await writeFolderProject(root, to, text);
  if (saved.name !== from) await root.removeEntry(from);
  return saved;
}

// -------------------------------------------------------------- conflicts

export interface Baseline {
  modified: number;
  size: number;
}

export type ConflictState = 'clean' | 'modified' | 'missing';

/**
 * Compares the file on disk against the baseline captured when it was opened/saved.
 * `missing` means it was renamed or deleted outside the app — the document must not be
 * silently overwritten.
 */
export async function checkConflict(
  root: DirectoryHandleLike,
  name: string,
  baseline: Baseline | null,
): Promise<ConflictState> {
  if (!baseline) return 'clean';
  try {
    const handle = await root.getFileHandle(name);
    const file = await handle.getFile();
    if (file.lastModified === baseline.modified && file.size === baseline.size) return 'clean';
    return 'modified';
  } catch {
    return 'missing';
  }
}
